import GeneralUtils from "../../utils/GeneralUtils";

const DEFAULT_TOKENS = 3000;
const DEFAULT_TEMPERATURE = 0.2;
const DEFAULT_TIMEOUT_MS = 30000;

const OPENROUTER_URL = 'https://openrouter.ai/api/v1/chat/completions';
const mapOpenRouterModel = (id) => {
    switch (id) {
        case 'llama-3-8b':
            return 'meta-llama/llama-3.1-8b-instruct';
        case 'claude-3-5-sonnet':
            return 'anthropic/claude-3.5-sonnet';
        case 'gemini-1.5-pro':
            return 'google/gemini-1.5-pro';
        default:
            return id;
    }
};

const AI_ENDPOINTS = {
    'gpt-3.5-turbo': {
        url: 'https://api.openai.com/v1/chat/completions',
        getHeaders: (apiKey) => ({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
        }),
        formatBody: ({ model, messages, max_tokens = DEFAULT_TOKENS, temperature = DEFAULT_TEMPERATURE }) => ({
            model,
            messages,
            max_tokens,
            temperature,
        }),
        requiresApiKey: true,
    },
    'gpt-4o': {
        url: 'https://api.openai.com/v1/chat/completions',
        getHeaders: (apiKey) => ({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
        }),
        formatBody: ({ model, messages, max_tokens = DEFAULT_TOKENS, temperature = DEFAULT_TEMPERATURE }) => ({
            model,
            messages,
            max_tokens,
            temperature,
        }),
        requiresApiKey: true,
    },
    'gpt-4o-mini': {
        url: 'https://api.openai.com/v1/chat/completions',
        getHeaders: (apiKey) => ({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
        }),
        formatBody: ({ model, messages, max_tokens = DEFAULT_TOKENS, temperature = DEFAULT_TEMPERATURE }) => ({
            model,
            messages,
            max_tokens,
            temperature,
        }),
        requiresApiKey: true,
    },
    'deepseek-chat': {
        url: 'https://api.deepseek.com/v1/chat/completions',
        getHeaders: (apiKey) => ({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
        }),
        formatBody: ({ model, messages, max_tokens = DEFAULT_TOKENS, temperature = DEFAULT_TEMPERATURE }) => ({
            model,
            messages,
            max_tokens,
            temperature,
        }),
        requiresApiKey: true,
    },
    'llama-3-8b': {
        url: OPENROUTER_URL,
        getHeaders: (apiKey) => {
            const headers = { 'Content-Type': 'application/json' };
            if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;
            return headers;
        },
        formatBody: ({ model, messages, max_tokens = 2048, temperature = DEFAULT_TEMPERATURE }) => ({
            model: mapOpenRouterModel(model),
            messages,
            max_tokens,
            temperature,
        }),
        requiresApiKey: false,
    },
    'claude-3-5-sonnet': {
        url: OPENROUTER_URL,
        getHeaders: (apiKey) => {
            const headers = { 'Content-Type': 'application/json' };
            if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;
            return headers;
        },
        formatBody: ({ model, messages, max_tokens = 3000, temperature = DEFAULT_TEMPERATURE }) => ({
            model: mapOpenRouterModel(model),
            messages,
            max_tokens,
            temperature,
        }),
        requiresApiKey: false,
    },
    'gemini-1.5-pro': {
        url: OPENROUTER_URL,
        getHeaders: (apiKey) => {
            const headers = { 'Content-Type': 'application/json' };
            if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;
            return headers;
        },
        formatBody: ({ model, messages, max_tokens = 3000, temperature = DEFAULT_TEMPERATURE }) => ({
            model: mapOpenRouterModel(model),
            messages,
            max_tokens,
            temperature,
        }),
        requiresApiKey: false,
    },
    // Add more models here as needed
};

export class AiService {
    async chat({ model, messages, apiKey, max_tokens, temperature, timeoutMs = DEFAULT_TIMEOUT_MS }) {
        const endpoint = AI_ENDPOINTS[model];
        if (!endpoint) throw new Error('Unsupported model');
        if (endpoint.requiresApiKey && !apiKey) throw new Error('API key required for this model');

        const body = endpoint.formatBody({ model, messages, max_tokens, temperature });
        const headers = endpoint.getHeaders(apiKey);

        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const response = await fetch(endpoint.url, {
                method: 'POST',
                headers,
                body: JSON.stringify(body),
                signal: controller.signal,
            });
            clearTimeout(timer);
            if (!response.ok) {
                let detail = 'AI API error';
                try {
                    const err = await response.json();
                    detail = err?.error?.message || err?.message || detail;
                } catch { /* ignore parse errors */ }
                throw new Error(detail);
            }
            const data = await response.json();
            const content = data?.choices?.[0]?.message?.content?.trim?.();
            if (!content) throw new Error('Empty response from AI');
            return { content };
        } catch (error) {
            GeneralUtils.notification(error);
            throw error;
        } finally {
            clearTimeout(timer);
        }
    }
}

export default new AiService();